import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  Avatar,
  styled,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import axios from 'axios';
import { USER_URLS } from '../config/urls';

// Styled components
const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  boxShadow: 'none',
  '&.MuiButton-contained': {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: 'none'
    }
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    }
  }
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.grey[600],
  fontSize: '2rem',
  fontWeight: 500
}));

const ProfilePage = () => {
  // State declarations
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    organisation: '',
    role: '',
    phone: '',
    bio: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Effect to fetch profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch current user profile
        const response = await axios.get(USER_URLS.getMe, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // console.log(response.data)
        // Populate all user fields from response
        setProfile({
          first_name: response.data.first_name || '',
          last_name: response.data.last_name || '',
          email: response.data.email || '',
          organisation: response.data.organisation || '',
          role: response.data.role || '',
          phone: response.data.phone || '',
          bio: response.data.bio || ''
        });
      } catch (err) {
        setError(err.message);
        console.error('Error fetching profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Profile update handler
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Only send fields that can be updated
      const updatedProfile = {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        bio: profile.bio
      };
      
      // Log the data being sent (remove in production)
      console.log('Updating profile with data:', updatedProfile);
      
      await axios.put(
        USER_URLS.updateUser,
        updatedProfile,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setSuccess(true);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Close snackbar handler
  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#FFFFFF' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          mb: 2
        }}>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 500, 
              mb: 1,
              fontSize: '1.5rem'
            }}>
              Profile Settings
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontSize: '0.875rem'
            }}>
              Update your personal information and profile details
            </Typography>
          </Box>
        </Box>
        <Box sx={{ 
          height: '2px', 
          bgcolor: '#F2F2F2', 
          width: '78%'
        }} />
      </Box>

      {/* Profile Form */}
      <form onSubmit={handleProfileUpdate}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', mb: 4, alignItems: 'center' }}>
            <Box sx={{ position: 'relative', mr: 3 }}>
              <StyledAvatar>
                {profile.first_name?.[0]}{profile.last_name?.[0]}
              </StyledAvatar>
              <IconButton 
                aria-label="upload picture" 
                component="label"
                sx={{ 
                  position: 'absolute', 
                  bottom: -5, 
                  right: -5,
                  backgroundColor: 'white',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <input hidden accept="image/*" type="file" />
                <PhotoCameraIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {`${profile.first_name} ${profile.last_name}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {profile.role}
              </Typography>
            </Box>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
            Personal Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="First Name"
                name="first_name"
                value={profile.first_name || ''}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
                placeholder="Enter your first name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={profile.last_name || ''}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
                placeholder="Enter your last name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Email"
                value={profile.email || ''}
                variant="outlined"
                size="small"
                disabled
                sx={{ '& .MuiInputBase-input.Mui-disabled': { 
                  WebkitTextFillColor: '#666',
                  bgcolor: '#f9f9f9'
                }}}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Phone"
                name="phone"
                value={profile.phone || ''}
                onChange={handleInputChange}
                variant="outlined"
                size="small"
                placeholder="Enter your phone number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Role"
                value={profile.role || ''}
                variant="outlined"
                size="small"
                disabled
                sx={{ '& .MuiInputBase-input.Mui-disabled': { 
                  WebkitTextFillColor: '#666',
                  bgcolor: '#f9f9f9'
                }}}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Organisation"
                value={profile.organisation || ''}
                variant="outlined"
                size="small"
                disabled
                sx={{ '& .MuiInputBase-input.Mui-disabled': { 
                  WebkitTextFillColor: '#666',
                  bgcolor: '#f9f9f9'
                }}}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Bio"
                name="bio"
                value={profile.bio || ''}
                onChange={handleInputChange}
                variant="outlined"
                multiline
                rows={4}
                placeholder="Write a short bio about yourself"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <StyledButton
            variant="contained"
            type="submit"
            disabled={saving}
            sx={{ minWidth: 120 }}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </StyledButton>
        </Box>
      </form>

      {/* Success/Error messages */}
      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Profile updated successfully!
        </Alert>
      </Snackbar>
      
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;